// RemoteSession: connection to one cloud browser (WebSocket for signaling/control,
// WebRTC for low-latency video/audio + input DataChannel, CDP screencast fallback).
import type { Api, SessionInfo } from './api';
import type { ClientMsg, InputMsg, MediaKind, ServerMsg } from './protocol';

export type ConnState = 'connecting' | 'connected' | 'reconnecting' | 'replaced' | 'closed';
export type StreamMode = 'webrtc' | 'screencast';

type Handler = (m: ServerMsg) => void;

// transceiver order defined by the agent's offer
const TX_SEND: Record<MediaKind, number> = { camera: 2, microphone: 3, screen: 4 };

export interface SessionCallbacks {
  onMessage: Handler;
  onState: (s: ConnState) => void;
  onMode: (m: StreamMode) => void;
  onStream: (s: MediaStream) => void;
  onFrame: (jpegBase64: string) => void;
}

export class RemoteSession {
  ws?: WebSocket;
  pc?: RTCPeerConnection;
  dc?: RTCDataChannel;
  mode: StreamMode;
  info: SessionInfo;
  private closed = false;
  private retries = 0;
  private rtcTimer?: ReturnType<typeof setTimeout>;
  private pingTimer?: ReturnType<typeof setInterval>;
  private pendingIce: RTCIceCandidateInit[] = [];
  private localTracks: Partial<Record<MediaKind, MediaStreamTrack>> = {};
  private size = { width: 1280, height: 720 };
  private sitePermissions: { origin: string; kind: string; allow: boolean }[] = [];

  constructor(
    private api: Api,
    info: SessionInfo,
    private cb: SessionCallbacks,
    private recreate: () => Promise<SessionInfo>,
    forceScreencast = false,
  ) {
    this.info = info;
    this.mode = forceScreencast || typeof RTCPeerConnection === 'undefined' ? 'screencast' : 'webrtc';
  }

  setSitePermissions(list: { origin: string; kind: string; allow: boolean }[]) {
    this.sitePermissions = list;
  }

  connect(width: number, height: number) {
    this.size = { width, height };
    this.closed = false;
    this.cb.onState(this.retries ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(this.api.wsUrl(`/api/sessions/${this.info.id}/ws`), this.api.wsProtocols());
    this.ws = ws;
    ws.onopen = () => {
      this.send({ t: 'hello', width: this.size.width, height: this.size.height, mode: this.mode, sitePermissions: this.sitePermissions });
      clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => this.send({ t: 'ping' }), 20_000);
      if (this.mode === 'webrtc') {
        // If no media path is established quickly (TURN blocked, UDP firewalled…), fall back.
        clearTimeout(this.rtcTimer);
        this.rtcTimer = setTimeout(() => {
          if (this.pc?.connectionState !== 'connected') this.fallbackToScreencast();
        }, 12_000);
      } else {
        this.cb.onState('connected');
      }
    };
    ws.onmessage = (ev) => {
      let m: ServerMsg;
      try {
        m = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.handle(m);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      clearInterval(this.pingTimer);
      this.teardownRtc();
      if (this.closed) return;
      if (ev.code === 4404 || ev.code === 1008) {
        // session is gone (idle timeout, server restart): start a fresh one
        this.recreate()
          .then((info) => {
            this.info = info;
            this.retry();
          })
          .catch(() => this.retry());
        return;
      }
      this.retry();
    };
  }

  private retry() {
    if (this.closed) return;
    this.cb.onState('reconnecting');
    const delay = Math.min(10_000, 500 * 2 ** this.retries++);
    setTimeout(() => !this.closed && this.connect(this.size.width, this.size.height), delay);
  }

  /** Reconnect immediately (e.g. app returned from background). */
  reconnectNow() {
    if (this.closed) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN && (this.mode === 'screencast' || this.pc?.connectionState === 'connected')) return;
    this.retries = 0;
    this.ws?.close();
    this.connect(this.size.width, this.size.height);
  }

  private handle(m: ServerMsg) {
    switch (m.t) {
      case 'offer':
        this.onOffer(m.sdp).catch((e) => {
          console.error('webrtc offer failed', e);
          this.fallbackToScreencast();
        });
        return;
      case 'ice':
        if (this.pc?.remoteDescription) this.pc.addIceCandidate(m.candidate).catch(() => {});
        else this.pendingIce.push(m.candidate);
        return;
      case 'frame':
        this.cb.onFrame(m.data);
        return;
      case 'replaced':
        this.closed = true;
        this.cb.onState('replaced');
        return;
    }
    this.cb.onMessage(m);
  }

  private async onOffer(sdp: string) {
    this.teardownRtc();
    const pc = new RTCPeerConnection({
      iceServers: this.info.iceServers,
      // The server only has relay candidates; forcing relay locally avoids useless checks.
      iceTransportPolicy: 'relay',
      bundlePolicy: 'max-bundle',
    });
    this.pc = pc;
    const stream = new MediaStream();
    pc.ontrack = (e) => {
      if (e.track.kind === 'video' || e.track.kind === 'audio') {
        if (e.transceiver === pc.getTransceivers()[0] || e.transceiver === pc.getTransceivers()[1]) {
          stream.addTrack(e.track);
          this.cb.onStream(stream);
        }
      }
    };
    pc.onicecandidate = (e) => e.candidate && this.send({ t: 'ice', candidate: e.candidate.toJSON() });
    pc.ondatachannel = (e) => {
      this.dc = e.channel;
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'connected') {
        clearTimeout(this.rtcTimer);
        this.retries = 0;
        this.cb.onState('connected');
        this.cb.onMode('webrtc');
      } else if (s === 'failed') {
        this.fallbackToScreencast();
      }
    };
    await pc.setRemoteDescription({ type: 'offer', sdp });
    const tx = pc.getTransceivers();
    for (const kind of Object.keys(TX_SEND) as MediaKind[]) {
      const t = tx[TX_SEND[kind]];
      if (!t) continue;
      t.direction = 'sendonly';
      if (kind !== 'microphone') preferVP8(t);
      const track = this.localTracks[kind];
      if (track) await t.sender.replaceTrack(track);
    }
    // low-latency playout for the remote screen
    for (const r of pc.getReceivers()) {
      try {
        (r as any).playoutDelayHint = 0;
        (r as any).jitterBufferTarget = 0;
      } catch {
        /* unsupported */
      }
    }
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    this.send({ t: 'answer', sdp: pc.localDescription!.sdp });
    for (const c of this.pendingIce.splice(0)) pc.addIceCandidate(c).catch(() => {});
  }

  private fallbackToScreencast() {
    if (this.mode === 'screencast' || this.closed) return;
    console.warn('WebRTC unavailable – falling back to CDP screencast over WebSocket');
    this.mode = 'screencast';
    this.teardownRtc();
    this.send({ t: 'screencast', on: true });
    this.cb.onMode('screencast');
    this.cb.onState('connected');
  }

  private teardownRtc() {
    clearTimeout(this.rtcTimer);
    this.dc = undefined;
    this.pc?.close();
    this.pc = undefined;
    this.pendingIce = [];
  }

  send(m: ClientMsg) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  /** Input goes over the DataChannel when available (lowest latency). */
  input(m: InputMsg) {
    if (this.dc?.readyState === 'open') this.dc.send(JSON.stringify(m));
    else this.send(m);
  }

  resize(width: number, height: number) {
    this.size = { width, height };
    this.send({ t: 'resize', width, height });
  }

  /** Attach (or detach with null) a local camera/mic/screen track to the upstream transceiver. */
  async setLocalTrack(kind: MediaKind, track: MediaStreamTrack | null) {
    const old = this.localTracks[kind];
    if (old && old !== track) old.stop();
    if (track) this.localTracks[kind] = track;
    else delete this.localTracks[kind];
    const t = this.pc?.getTransceivers()[TX_SEND[kind]];
    if (t) await t.sender.replaceTrack(track).catch(() => {});
  }

  hasLocalTrack(kind: MediaKind) {
    return !!this.localTracks[kind];
  }

  close() {
    this.closed = true;
    clearInterval(this.pingTimer);
    for (const k of Object.keys(this.localTracks) as MediaKind[]) this.localTracks[k]?.stop();
    this.localTracks = {};
    this.teardownRtc();
    this.ws?.close();
    this.cb.onState('closed');
  }
}

function preferVP8(t: RTCRtpTransceiver) {
  try {
    const caps = RTCRtpSender.getCapabilities?.('video');
    if (!caps || !t.setCodecPreferences) return;
    const vp8 = caps.codecs.filter((c) => /vp8/i.test(c.mimeType));
    const rest = caps.codecs.filter((c) => !/vp8/i.test(c.mimeType));
    if (vp8.length) t.setCodecPreferences([...vp8, ...rest]);
  } catch {
    /* ignore */
  }
}
